import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Edit, Trash2, Search, IndianRupee, Key, ChevronDown, ChevronUp, Eye, Download, FileText } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [editFormData, setEditFormData] = useState({});
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [userPredictions, setUserPredictions] = useState({});
  const [loadingPredictions, setLoadingPredictions] = useState({});
  const [viewPredictionDialog, setViewPredictionDialog] = useState({ open: false, prediction: null });
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role_id: ''
  });
  const [roles, setRoles] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users?limit=1000`);
      // Handle new paginated response format
      setUsers(response.data.users || response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/roles`);
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const handleCreateUser = async () => {
    try {
      // Validation
      if (!newUserData.name || !newUserData.email || !newUserData.mobile || !newUserData.password) {
        toast({
          title: "Error",
          description: "Please fill all required fields",
          variant: "destructive"
        });
        return;
      }

      if (newUserData.mobile.length !== 10) {
        toast({
          title: "Error",
          description: "Mobile number must be 10 digits",
          variant: "destructive"
        });
        return;
      }

      if (newUserData.password.length < 6) {
        toast({
          title: "Error",
          description: "Password must be at least 6 characters",
          variant: "destructive"
        });
        return;
      }

      const response = await axios.post(`${BACKEND_URL}/api/admin/users`, newUserData);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: `User created successfully! Referral Code: ${response.data.referral_code}`
        });
        setCreateUserDialog(false);
        setNewUserData({ name: '', email: '', mobile: '', password: '', role_id: '' });
        fetchUsers();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create user",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (user) => {
    setEditDialog({ open: true, user });
    setEditFormData({
      name: user.name,
      email: user.email,
      mobile: user.mobile,
      earnings: user.earnings || 0,
      referrals_count: user.referrals_count || 0
    });
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${editDialog.user.id}`,
        editFormData
      );
      toast({
        title: "Success",
        description: "User updated successfully"
      });
      setEditDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update user",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userName}?`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`);
      toast({
        title: "Success",
        description: "User deleted successfully"
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  const handleResetPassword = (user) => {
    setResetPasswordDialog({ open: true, user });
    setNewPassword('');
    setSendEmail(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${resetPasswordDialog.user.id}/reset-password`,
        {
          new_password: newPassword,
          send_email: sendEmail
        }
      );
      
      toast({
        title: "Success",
        description: sendEmail 
          ? "Password reset and email sent to user" 
          : "Password reset successfully"
      });
      
      setResetPasswordDialog({ open: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reset password",
        variant: "destructive"
      });
    }
  };

  const toggleUserPredictions = async (userId) => {
    if (expandedUsers[userId]) {
      // Collapse
      setExpandedUsers(prev => ({ ...prev, [userId]: false }));
    } else {
      // Expand and fetch predictions
      setExpandedUsers(prev => ({ ...prev, [userId]: true }));
      if (!userPredictions[userId]) {
        await fetchUserPredictions(userId);
      }
    }
  };

  const fetchUserPredictions = async (userId) => {
    setLoadingPredictions(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await axios.get(`${BACKEND_URL}/api/predictions/${userId}`);
      setUserPredictions(prev => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load predictions",
        variant: "destructive"
      });
    } finally {
      setLoadingPredictions(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDeletePrediction = async (userId, predictionId) => {
    if (!window.confirm('Are you sure you want to delete this prediction?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/predictions/${predictionId}`);
      toast({
        title: "Success",
        description: "Prediction deleted successfully"
      });
      // Refresh predictions for this user
      await fetchUserPredictions(userId);
      fetchUsers(); // Refresh user stats
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete prediction",
        variant: "destructive"
      });
    }
  };

  const handleViewPrediction = (prediction) => {
    setViewPredictionDialog({ open: true, prediction });
  };

  const handleDownloadUserPDF = (user, prediction) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(37, 99, 235);
      doc.text('College Kampus - JEE Predictor', 105, 15, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('User Prediction Report', 105, 22, { align: 'center' });
      
      // User info
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`User: ${user.name}`, 15, 32);
      doc.text(`Email: ${user.email}`, 15, 38);
      doc.text(`Exam: ${prediction.exam_type}`, 15, 44);
      doc.text(`Rank: ${prediction.rank}`, 105, 32);
      doc.text(`Category: ${prediction.category}`, 105, 38);
      doc.text(`Date: ${new Date(prediction.created_at).toLocaleDateString()}`, 105, 44);
      
      // If predictions data exists
      if (prediction.predictions && prediction.predictions.length > 0) {
        let y = 55;
        const lineHeight = 6;
        
        // Table header
        doc.setFillColor(37, 99, 235);
        doc.rect(10, y, 190, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.text('#', 12, y + 5);
        doc.text('College', 25, y + 5);
        doc.text('Branch', 110, y + 5);
        doc.text('Cutoff', 155, y + 5);
        doc.text('Chance', 180, y + 5);
        
        y += 10;
        doc.setTextColor(0, 0, 0);
        
        // Table rows
        prediction.predictions.slice(0, 30).forEach((result, index) => {
          if (y > 280) {
            doc.addPage();
            y = 20;
          }
          
          // Extract data with proper fallbacks (matching Results.jsx logic)
          const collegeName = (result.name || result.collegeName || result.college || 'Unknown').substring(0, 40);
          const branch = (result.branch || result.branchCode || 'N/A').substring(0, 20);
          const cutoff = (result.cutoff || result.cutoffRank || 'N/A').toString();
          const chance = (result.chance || 'Medium');
          
          doc.setFontSize(8);
          doc.text((index + 1).toString(), 12, y);
          doc.text(collegeName, 25, y);
          doc.text(branch, 110, y);
          doc.text(cutoff, 155, y);
          doc.text(chance, 180, y);
          
          doc.setDrawColor(200, 200, 200);
          doc.line(10, y + 2, 200, y + 2);
          
          y += lineHeight;
        });
      }
      
      doc.save(`Admin_${user.name.replace(/\s+/g, '_')}_Prediction_${Date.now()}.pdf`);
      toast({ title: "Success!", description: "PDF downloaded successfully" });
    } catch (error) {
      console.error('PDF error:', error);
      toast({ title: "Error", description: "Failed to generate PDF", variant: "destructive" });
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.referral_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>User Management</CardTitle>
              <CardDescription>Manage all registered users</CardDescription>
            </div>
            <Button onClick={() => setCreateUserDialog(true)}>
              Create New User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or referral code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">Total Users</p>
              <p className="text-2xl font-bold text-blue-600">{users.length}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">Total Earnings Distributed</p>
              <p className="text-2xl font-bold text-green-600 flex items-center">
                <IndianRupee className="w-5 h-5" />
                {users.reduce((sum, user) => sum + (user.earnings || 0), 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">Total Referrals Made</p>
              <p className="text-2xl font-bold text-purple-600">
                {users.reduce((sum, user) => sum + (user.referrals_count || 0), 0)}
              </p>
            </div>
          </div>

          {/* Users Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Mobile</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Role</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referral Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Earnings</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Referrals</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Predictions</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Joined</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-slate-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    currentUsers.map((user) => (
                      <React.Fragment key={user.id}>
                      <tr className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{user.name}</td>
                        <td className="px-4 py-3 text-sm">{user.email}</td>
                        <td className="px-4 py-3 text-sm">{user.mobile}</td>
                        <td className="px-4 py-3">
                          {user.role_name ? (
                            <Badge variant="secondary" className="capitalize">
                              {user.role_name}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">No Role</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="font-mono">
                            {user.referral_code}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-green-600">
                          ₹{(user.earnings || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">{user.referrals_count || 0}</td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleUserPredictions(user.id)}
                            className="gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            View
                            {expandedUsers[user.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </Button>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(user)}
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => handleResetPassword(user)}
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(user.id, user.name)}
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Predictions Row */}
                      {expandedUsers[user.id] && (
                        <tr className="bg-slate-50">
                          <td colSpan="9" className="px-4 py-4">
                            {loadingPredictions[user.id] ? (
                              <div className="flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <span className="ml-2 text-sm text-slate-600">Loading predictions...</span>
                              </div>
                            ) : userPredictions[user.id] && userPredictions[user.id].length > 0 ? (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm">User Predictions ({userPredictions[user.id].length})</h4>
                                {userPredictions[user.id].map((prediction) => (
                                  <div key={prediction.id} className="bg-white p-3 rounded border">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium">
                                          {prediction.exam_type} - Rank: {prediction.rank}
                                        </p>
                                        <p className="text-xs text-slate-600">
                                          Category: {prediction.category} | 
                                          Created: {new Date(prediction.created_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleViewPrediction(prediction)}
                                          title="View Details"
                                        >
                                          <Eye className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleDownloadUserPDF(user, prediction)}
                                          title="Download PDF"
                                        >
                                          <Download className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-red-600"
                                          onClick={() => handleDeletePrediction(user.id, prediction.id)}
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-600 text-center py-4">No predictions found for this user</p>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-4">
              <div className="text-sm text-slate-600">
                Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                
                {/* Page Numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          size="sm"
                          variant={currentPage === page ? "default" : "outline"}
                          onClick={() => handlePageChange(page)}
                          className="w-10"
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 py-1">...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editFormData.name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input
                value={editFormData.mobile || ''}
                onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
              />
            </div>
            <div>
              <Label>Earnings (₹)</Label>
              <Input
                type="number"
                value={editFormData.earnings || 0}
                onChange={(e) => setEditFormData({ ...editFormData, earnings: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label>Referrals Count</Label>
              <Input
                type="number"
                value={editFormData.referrals_count || 0}
                onChange={(e) => setEditFormData({ ...editFormData, referrals_count: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, user: null })}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => setResetPasswordDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordDialog.user?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>User:</strong> {resetPasswordDialog.user?.name} ({resetPasswordDialog.user?.email})
              </p>
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <p className="text-xs text-slate-500 mt-1">
                Minimum 6 characters required
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
              />
              <label
                htmlFor="send-email"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Send password reset notification to user's email
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setResetPasswordDialog({ open: false, user: null })}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleResetPasswordSubmit}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Key className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Prediction Dialog */}
      <Dialog 
        open={viewPredictionDialog.open} 
        onOpenChange={(open) => setViewPredictionDialog({ open, prediction: null })}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prediction Details</DialogTitle>
            <DialogDescription>
              Complete prediction information
            </DialogDescription>
          </DialogHeader>
          {viewPredictionDialog.prediction && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-600">Exam Type</p>
                  <p className="text-sm font-medium">{viewPredictionDialog.prediction.exam_type}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Rank</p>
                  <p className="text-sm font-medium">{viewPredictionDialog.prediction.rank}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Category</p>
                  <p className="text-sm font-medium">{viewPredictionDialog.prediction.category}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Gender</p>
                  <p className="text-sm font-medium">{viewPredictionDialog.prediction.gender || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">State</p>
                  <p className="text-sm font-medium">{viewPredictionDialog.prediction.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Created At</p>
                  <p className="text-sm font-medium">
                    {new Date(viewPredictionDialog.prediction.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Filters Applied */}
              {viewPredictionDialog.prediction.filters && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Filters Applied:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(viewPredictionDialog.prediction.filters).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-slate-600">{key}:</span>{' '}
                        <span className="font-medium">{JSON.stringify(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Predictions Table */}
              {viewPredictionDialog.prediction.predictions && 
               viewPredictionDialog.prediction.predictions.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">
                    College Predictions ({viewPredictionDialog.prediction.predictions.length})
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-left">College</th>
                            <th className="px-3 py-2 text-left">Branch</th>
                            <th className="px-3 py-2 text-left">Location</th>
                            <th className="px-3 py-2 text-left">Cutoff Rank</th>
                            <th className="px-3 py-2 text-left">Fees</th>
                            <th className="px-3 py-2 text-left">Chance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewPredictionDialog.prediction.predictions.map((pred, index) => (
                            <tr key={index} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2">{index + 1}</td>
                              <td className="px-3 py-2">{pred.college || pred.name || 'N/A'}</td>
                              <td className="px-3 py-2">{pred.branch || 'N/A'}</td>
                              <td className="px-3 py-2">
                                {pred.city && pred.state 
                                  ? `${pred.city}, ${pred.state}` 
                                  : pred.state || pred.city || 'N/A'}
                              </td>
                              <td className="px-3 py-2">{pred.cutoff || 'N/A'}</td>
                              <td className="px-3 py-2">₹{pred.fees || 'N/A'}</td>
                              <td className="px-3 py-2">
                                <Badge 
                                  variant={
                                    pred.chance === 'High' ? 'default' : 
                                    pred.chance === 'Medium' ? 'secondary' : 
                                    'outline'
                                  }
                                >
                                  {pred.chance || 'Medium'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setViewPredictionDialog({ open: false, prediction: null })}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onOpenChange={setCreateUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system and assign a role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                placeholder="Enter full name"
                value={newUserData.name}
                onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Mobile Number *</Label>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={newUserData.mobile}
                onChange={(e) => setNewUserData({ ...newUserData, mobile: e.target.value })}
                maxLength={10}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="Minimum 6 characters"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Assign Role (Optional)</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={newUserData.role_id}
                onChange={(e) => setNewUserData({ ...newUserData, role_id: e.target.value })}
              >
                <option value="">No Role (Regular User)</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
              <p className="font-medium">Note:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>A unique referral code will be auto-generated</li>
                <li>User can login immediately with these credentials</li>
                <li>Mobile number must be unique</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreateUser} className="flex-1">
                Create User
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setCreateUserDialog(false);
                  setNewUserData({ name: '', email: '', mobile: '', password: '', role_id: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
